import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import compressor from 'browser-image-compression';
import * as Yup from 'yup';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { v4 } from 'uuid';
import { DB } from '~/services/firebase';
import { useAuth } from '~/hooks/auth';

import AWSBucket, { AWSParams } from '~/services/aws';

import Dashboard from '~/layouts/Dashboard';
import getValidationErrors from '~/utils/getValidationErrors';
import IProduct from '~/interfaces/IProduct';
import { useToast } from '~/hooks/toast';
import { useCategoriesCompany } from '~/hooks/categoriesCompany';
import { useProductsCompany } from '~/hooks/productsCompany';
import { bff } from '~/services/bff';
import { useSupplies } from '~/hooks/supplies';

interface Params {
  id: string;
}

interface SupplieData {
  uid: string;
  quantity: number;
}

const { REACT_APP_AWS_BUCKET_URL, REACT_APP_FIREBASE_PROJECT } = process.env;

const { $ } = window as any;

const ProductForm: React.FC = () => {
  const history = useHistory();
  const { state } = useLocation();
  const params = useParams<Params>();
  const { user, company } = useAuth();
  const { addToast } = useToast();
  const { categories, loadCategories } = useCategoriesCompany();
  const { updateProducts, loadComplements, complements, products } =
    useProductsCompany();

  const [data, setData] = useState({} as IProduct);
  const [errors, setErrors] = useState({} as any);
  const [loading, setLoading] = useState(false);
  const [updateScript, setUpdateScript] = useState(false);
  const { id } = params;

  const [productSupplie, setProductSupplie] = useState({} as SupplieData);
  const [productSupplies, setProductsSupllies] = useState([] as SupplieData[]);
  const [tab, setTab] = useState(0);
  const { loadSupplies, isInitialized, supplies } = useSupplies();

  useEffect(() => {
    if (company.uid && !isInitialized) {
      loadSupplies(company.uid);
    }
  }, [loadSupplies, company.uid, isInitialized]);

  useEffect(() => {
    async function loadProduct() {
      const productsRef = doc(DB, 'products', id);
      const docSnap = await getDoc(productsRef);
      const item = docSnap.data() as IProduct;
      setData({
        ...item,
        uid: id,
        category_id: item.category.uid,
      } as IProduct);

      setUpdateScript(true);
      if (item.supplies) setProductsSupllies(item.supplies);

      setTimeout(() => {
        $('#complements').val(item.groups_complements);
        $('#complements').trigger('change');
      }, 100);
    }

    if (state) {
      const _item = state as IProduct;
      setData({
        ..._item,
        category_id: _item.category.uid,
      });

      const element = document.getElementById('measure') as any;
      if (element && _item.measure) {
        element.value = _item.measure;
      }

      setUpdateScript(true);
      setTimeout(() => {
        $('#complements').val(_item.groups_complements);
        $('#complements').trigger('change');
      }, 100);
    } else if (id) {
      loadProduct();
    } else {
      setData({ status: true, description: '', measure: 'UN' } as IProduct);
      setUpdateScript(true);
    }
  }, [id, state]);

  useEffect(() => {
    loadCategories();

    const element = document.getElementById('category') as any;

    if (element && data.category) {
      element.value = data.category.uid;
    }
  }, [user.uid, data.category, loadCategories]);

  useEffect(() => {
    async function init() {
      if (!complements) {
        await loadComplements();

        setTimeout(() => {
          $('#complements').val(data.groups_complements);
          $('#complements').trigger('change');
        }, 100);
      }
    }

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadComplements]);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      let _complements = $('#complements').select2('data');
      _complements = _complements.map((item: any) => item.id);

      try {
        const schema = Yup.object().shape({
          name: Yup.string().required('O nome do produto é obrigatória'),
          value: Yup.string().required('O valor é obrigatória'),
          category_id: Yup.string().required('A categoria é obrigatória'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const { name, description, value, category_id, status, measure } = data;

        const findCategory = categories.find(item => item.uid === category_id);

        const inputFile = document.getElementById('photo') as HTMLInputElement;

        let image = '';

        // if (inputFile && inputFile.files && inputFile.files.length > 0) {
        //   const file = inputFile.files[0];
        //   const storageRef = Storage.ref(`products/${v4()}.png`);
        //   const resposne = await storageRef.put(file);
        //   image = await resposne.ref.getDownloadURL();
        // }

        if (inputFile && inputFile.files && inputFile.files.length > 0) {
          const file = inputFile.files[0];
          const filename = `${company.fantasy}-${company.uid}/${v4()}.png`;

          const compressedFile = await compressor(file, {
            maxSizeMB: 1,
            maxWidthOrHeight: 300,
            useWebWorker: true,
          });

          let config: any = {
            ...AWSParams,
            Body: compressedFile,
            Key: filename,
          };

          AWSBucket.putObject(config).send();

          if (data.uid && data.image) {
            const Key = data.image.replace(`${REACT_APP_AWS_BUCKET_URL}/`, '');
            config = {
              ...AWSParams,
              Key,
            };

            AWSBucket.deleteObject(config, (err, tmp) => {
              if (err) console.log(err);
              else console.log();
            });
          }

          image = `${REACT_APP_AWS_BUCKET_URL}/${filename}`;
        }

        const uid = data.uid || v4();

        let parseData: any = {
          ...data,
          uid,
          name,
          description,
          value,
          user_id: user.uid,
          company_id: company.uid,
          category: findCategory,
          status,
          measure: measure || 'UN',
          groups_complements: _complements || [],
          order: data.order ? data.order : products.length,
        };

        if (image) parseData = { ...parseData, image };

        if (data.uid) {
          await updateDoc(doc(DB, 'products', uid), parseData);
          updateProducts(parseData);
        } else {
          await setDoc(doc(DB, 'products', uid), parseData);
          updateProducts(parseData, true);
        }

        await bff.delete(`/products/${REACT_APP_FIREBASE_PROJECT}`);

        addToast({
          type: 'success',
          description: data.uid
            ? 'Produto alterado com sucesso!'
            : 'Produto criado com sucesso!',
        });

        history.push('/products');

        setLoading(false);
      } catch (err: any) {
        console.log(err);
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
        }
      }
    },
    [user, data, addToast, history, categories, company, updateProducts],
  );

  const handleSumitSupplie = useCallback(
    async (ev: any) => {
      ev.preventDefault();
      const items = [...productSupplies] as SupplieData[];

      items.push({
        uid: productSupplie.uid,
        quantity: productSupplie.quantity,
      });

      await updateDoc(doc(DB, 'products', data.uid), { supplies: items });
      await bff.delete(`/products/${REACT_APP_FIREBASE_PROJECT}`);
      updateProducts({ ...data, supplies: items });

      setProductsSupllies(items);
      setProductSupplie({ uid: '', quantity: 0 } as SupplieData);
    },
    [productSupplies, productSupplie, data, updateProducts],
  );

  const removeSupplie = useCallback(
    async (uid: string) => {
      const items = productSupplies.filter(item => item.uid !== uid);

      await updateDoc(doc(DB, 'products', data.uid), { supplies: items });
      await bff.delete(`/products/${REACT_APP_FIREBASE_PROJECT}`);
      updateProducts({ ...data, supplies: items });

      setProductsSupllies(items);
    },
    [data, productSupplies, updateProducts],
  );

  return (
    <Dashboard containerMessage="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
      <div className="row">
        <div className="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
          <div className="nk-block nk-block-lg">
            <div className="nk-block-head">
              <div className="nk-block-head-content">
                <div className="nk-block-head-sub">
                  <Link className="back-to" to="/products">
                    <em className="icon ni ni-arrow-left" />
                    <span>Voltar</span>
                  </Link>
                </div>
                <h4 className="title nk-block-title">
                  {id ? 'Editar Produto' : 'Criar Produto'}
                </h4>
                <div className="nk-block-des">
                  <p>Preencha o formulário abaixo e crie um novo produto.</p>
                </div>
              </div>
            </div>

            <div className="card card-bordered">
              <ul className="nav nav-tabs pl-4">
                <li className="nav-item">
                  <div
                    role="button"
                    onClick={() => setTab(0)}
                    className={`nav-link ${tab === 0 ? 'active' : ''}`}
                  >
                    Informações do produto
                  </div>
                </li>

                <li className="nav-item">
                  <div
                    role="button"
                    onClick={() => setTab(1)}
                    className={`nav-link ${tab === 1 ? 'active' : ''}`}
                  >
                    Composição
                  </div>
                </li>
              </ul>
              <div className="card-inner">
                {tab === 1 && (
                  <>
                    <form onSubmit={handleSumitSupplie}>
                      <div className="row">
                        <div className="col-sm-5">
                          <div className="form-group mb-3">
                            <label className="form-label">Insumo</label>
                            <div className="form-control-wrap ">
                              <div className="form-control-select">
                                <select
                                  className="form-control"
                                  value={productSupplie.uid}
                                  onChange={event => {
                                    setProductSupplie({
                                      ...productSupplie,
                                      uid: event.target.value,
                                    });
                                  }}
                                >
                                  <option value="">Selecione...</option>
                                  {supplies.map(item => (
                                    <option value={item.uid} key={item.uid}>
                                      {item.name}
                                    </option>
                                  ))}
                                </select>
                                {errors.product_supplie && (
                                  <span className="invalid">
                                    {errors.category_id}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-2">
                          <div className="form-group mb-3">
                            <label className="form-label">Quantidade</label>
                            <div className="form-control-wrap">
                              <input
                                type="number"
                                step=".01"
                                className="form-control"
                                value={productSupplie.quantity}
                                onChange={event => {
                                  setProductSupplie({
                                    ...productSupplie,
                                    quantity: parseFloat(event.target.value),
                                  });
                                }}
                              />
                              {errors.name && (
                                <span className="invalid">{errors.name}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="col-sm-3 align-items-end d-flex">
                          <div className="form-group mb-3">
                            <button
                              type="submit"
                              className="btn btn-md btn-primary"
                              disabled={loading}
                            >
                              {loading && (
                                <div
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                />
                              )}
                              {!loading && 'Adicionar'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                    {productSupplies.map(item => (
                      <div className="row" key={item.uid}>
                        <div className="col-sm-5">
                          <div className="form-group mb-3">
                            <div className="form-control-wrap ">
                              <div className="form-control-select">
                                <select
                                  disabled
                                  className="form-control"
                                  value={item.uid}
                                >
                                  <option value="">Selecione...</option>
                                  {supplies.map(item => (
                                    <option value={item.uid} key={item.uid}>
                                      {item.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-2">
                          <div className="form-group mb-3">
                            <div className="form-control-wrap">
                              <input
                                type="number"
                                step=".01"
                                className="form-control"
                                value={item.quantity}
                                readOnly
                                disabled
                              />
                            </div>
                          </div>
                        </div>

                        <div className="col-sm-2 align-items-end d-flex">
                          <button
                            type="button"
                            className="mb-3 btn"
                            onClick={() => removeSupplie(item.uid)}
                          >
                            <em className="icon ni ni-trash" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                <form
                  onSubmit={handleSubmit}
                  style={{ display: tab === 0 ? 'block' : 'none' }}
                >
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Nome do Produto</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.name}
                            onChange={event => {
                              setData({ ...data, name: event.target.value });
                            }}
                          />
                          {errors.name && (
                            <span className="invalid">{errors.name}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Valor</label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            step=".01"
                            className="form-control"
                            defaultValue={data.value}
                            onChange={event => {
                              setData({ ...data, value: event.target.value });
                            }}
                          />
                          {errors.value && (
                            <span className="invalid">{errors.value}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-6">
                      <div className="form-group mb-3">
                        <label className="form-label" htmlFor="category">
                          Categoria
                        </label>
                        <div className="form-control-wrap ">
                          <div className="form-control-select">
                            <select
                              className="form-control"
                              id="category"
                              value={data.category_id}
                              onChange={event => {
                                setData({
                                  ...data,
                                  category_id: event.target.value,
                                });
                              }}
                            >
                              <option value="">Selecione...</option>
                              {categories.map(category => (
                                <option value={category.uid} key={category.uid}>
                                  {category.name}
                                </option>
                              ))}
                            </select>
                            {errors.category_id && (
                              <span className="invalid">
                                {errors.category_id}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-6">
                      <div className="form-group mb-3">
                        <label className="form-label" htmlFor="default-06">
                          Valor por
                        </label>
                        <div className="form-control-wrap ">
                          <div className="form-control-select">
                            <select
                              className="form-control"
                              id="measure"
                              defaultValue={data.measure}
                              onChange={event => {
                                setData({
                                  ...data,
                                  measure: event.target.value,
                                });
                              }}
                            >
                              <option value="UN">Unidade</option>
                              <option value="KG">Kilo</option>
                            </select>
                            {errors.measure && (
                              <span className="invalid">{errors.measure}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Estoque Atual</label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            step=".01"
                            className="form-control"
                            defaultValue={data.stok}
                            onChange={event => {
                              setData({
                                ...data,
                                stok: parseFloat(event.target.value),
                              });
                            }}
                          />
                          {errors.value && (
                            <span className="invalid">{errors.stok}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Estoque Mínimo</label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            step=".01"
                            className="form-control"
                            defaultValue={data.stok_min}
                            onChange={event => {
                              setData({
                                ...data,
                                stok_min: parseFloat(event.target.value),
                              });
                            }}
                          />
                          {errors.value && (
                            <span className="invalid">{errors.stok_min}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-12 mb-3">
                      <div className="form-group">
                        <label className="form-label">Complementos</label>
                        <div className="form-control-wrap">
                          <select
                            id="complements"
                            className="form-select select2"
                            multiple
                            data-placeholder="Selecione..."
                          >
                            {complements?.map(item => (
                              <option key={item.uid} value={item.uid}>
                                {`${item.name} ${item.name2 ? '/' : ''} ${
                                  item.name2 || ''
                                }`}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* <div className="col-lg-12">
                      <div className="form-group">
                        <label className="form-label">Link da Imagem</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.image}
                            onChange={event => {
                              setData({ ...data, image: event.target.value });
                            }}
                          />
                          {errors.image && (
                            <span className="invalid">{errors.image}</span>
                          )}
                        </div>
                      </div>
                    </div> */}

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Imagem</label>
                        <div className="form-control-wrap">
                          <input
                            type="file"
                            id="photo"
                            style={{ width: '100%' }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Ordem</label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            className="form-control"
                            defaultValue={data.order}
                            onChange={event => {
                              setData({
                                ...data,
                                order: parseInt(event.target.value),
                              });
                            }}
                          />
                          {errors.order && (
                            <span className="invalid">{errors.order}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-12">
                      <div className="form-group mb-3">
                        <label
                          className="form-label"
                          htmlFor="default-textarea"
                        >
                          Descrição
                        </label>
                        <div className="form-control-wrap">
                          <textarea
                            className="form-control no-resize"
                            id="default-textarea"
                            placeholder="Informe uma descrição para o produto..."
                            defaultValue={data.description}
                            onChange={event => {
                              setData({
                                ...data,
                                description: event.target.value,
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-4">
                      <div className="form-group mb-3">
                        <label className="form-label">Status do produto?</label>
                        <ul className="custom-control-group g-3 align-center">
                          <li>
                            <div className="custom-control custom-control-sm custom-checkbox">
                              <input
                                id="active"
                                type="checkbox"
                                className="custom-control-input"
                                defaultChecked={data.status}
                                onClick={() => {
                                  setData({
                                    ...data,
                                    status: !data.status,
                                  });
                                }}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="active"
                              >
                                Ativo
                              </label>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="col-lg-4">
                      <div className="form-group mb-3">
                        <label className="form-label">
                          Habilitar Promoção?
                        </label>
                        <ul className="custom-control-group g-3 align-center">
                          <li>
                            <div className="custom-control custom-control-sm custom-checkbox">
                              <input
                                id="promotion"
                                type="checkbox"
                                className="custom-control-input"
                                defaultChecked={data.enable_promotion}
                                onClick={() => {
                                  setData({
                                    ...data,
                                    enable_promotion: !data.enable_promotion,
                                  });
                                }}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="promotion"
                              >
                                Ativo
                              </label>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="col-lg-4">
                      <div className="form-group mb-3">
                        <label className="form-label">Valor da Promoção</label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            step=".01"
                            className="form-control"
                            defaultValue={data.value_promotion}
                            onChange={event => {
                              setData({
                                ...data,
                                value_promotion: event.target.value,
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-4">
                      <div className="form-group mb-3">
                        <label className="form-label">Tipo pizza?</label>
                        <ul className="custom-control-group g-3 align-center">
                          <li>
                            <div className="custom-control custom-control-sm custom-checkbox">
                              <input
                                id="pizza_type"
                                type="checkbox"
                                className="custom-control-input"
                                defaultChecked={data.pizza_type}
                                onClick={() => {
                                  setData({
                                    ...data,
                                    pizza_type: !data.pizza_type,
                                  });
                                }}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="pizza_type"
                              >
                                Ativo
                              </label>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <button
                          type="submit"
                          className="btn btn-lg btn-primary"
                          disabled={loading}
                        >
                          {loading && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          )}
                          {!loading && 'Salvar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {updateScript && (
        <Helmet>
          <script src={`${process.env.PUBLIC_URL}/assets/js/masks.js`} />
        </Helmet>
      )}
    </Dashboard>
  );
};

export default ProductForm;
